/**
 * Activation-funnel event schemas (T2.8 / ISSUE-200 / ISSUE-201).
 * Browser-safe — no @nutians/db imports.
 */
import { z } from 'zod';
export const appEventNameSchema = z.enum([
    'signup_completed',
    'onboarding_step_completed',
    'onboarding_completed',
    'first_log_submitted',
    'log_submitted',
    'trial_started',
    'paywall_viewed',
    'premium_gate_reached',
    'premium_teaser_viewed',
    'premium_teaser_tapped',
    'paywall_cta_tapped',
    'checkout_started',
    'trial_value_reached',
    'subscription_started',
    'install_help_shown',
    'open_in_safari_prompted',
    'pwa_installed',
    'push_permission_granted',
    'chat_battery_emptied',
    'chat_battery_recharged',
    'check_in_earned',
    'referral_earned',
    'upsell_shown_from_battery',
    'upgrade_from_battery',
    'weight_logged',
    // Adaptive coaching events (T12.5 / ISSUE-072 / ADR-0026) — use coaching_ prefix, NOT check_in_
    'coaching_review_shown',
    'coaching_calibrating',
    'expenditure_estimated',
    'coaching_target_proposed',
    'coaching_target_accepted',
    'coaching_target_kept',
    'coaching_upsell_shown',
    'performance_layer_converted',
    'chat_message_metered',
    'action_confirmed',
    'action_rejected',
    'clarification_answered',
    'affirmation_shortcut_used',
    // Conversion funnel triggers (T15.3 / ISSUE-080)
    'logged_days_3of7',
    'weight_entries_7',
    'repeated_meal_detected',
    'plateau_detected',
    'protein_fibre_shortfall_repeated',
    'coaching_trial_started',
]);
export const appEventClientSchema = z.enum(['api', 'web', 'mobile']);
/** Body for POST /me/events — client beacon for events the server cannot know. */
export const trackEventSchema = z.object({
    name: appEventNameSchema,
    /** Structural props — no PII (step names, booleans, counts only). */
    props: z.record(z.string(), z.unknown()).default({}),
    /** Emission origin; defaults to 'web'. */
    client: appEventClientSchema.optional(),
});
// ── Admin activation metrics ──────────────────────────────────────────────────
/** Per-step counts returned by GET /admin/metrics/activation. */
export const activationFunnelStepSchema = z.object({
    name: appEventNameSchema,
    count: z.number().int().nonnegative(),
});
/**
 * Response for GET /admin/metrics/activation?window=30d|7d|all
 *
 * KPI targets (ISSUE-201 / 09-metrics):
 *   signup→onboarding completion ≥ 75%
 *   time-to-first-log ≤ 10 min median  (p50 in seconds)
 *   first-week logs ≥ 5
 */
export const adminActivationMetricsSchema = z.object({
    window: z.string(),
    /** Total signup_completed events in the window. */
    signups: z.number().int().nonnegative(),
    /** Total onboarding_completed events in the window. */
    onboardingCompleted: z.number().int().nonnegative(),
    /** signups > 0 ? onboardingCompleted / signups : null */
    onboardingRate: z.number().nonnegative().nullable(),
    /** Target: ≥ 75% (0.75) */
    onboardingRateTarget: z.literal(0.75),
    onboardingRateMet: z.boolean(),
    /** Total first_log_submitted events in the window. */
    firstLogs: z.number().int().nonnegative(),
    /** Median seconds from signup_completed → first_log_submitted.
     *  Null when fewer than 2 data points. */
    medianSecondsToFirstLog: z.number().nonnegative().nullable(),
    /** Target: ≤ 600 s (10 min) */
    medianSecondsToFirstLogTarget: z.literal(600),
    medianSecondsToFirstLogMet: z.boolean(),
    /** Per-step event counts for the funnel visualisation. */
    funnel: z.array(activationFunnelStepSchema),
    /** Upsell→trial→convert path: coaching_upsell_shown → coaching_trial_started → subscription_started. */
    upsellFunnel: z.array(activationFunnelStepSchema),
});
export const trialExperimentSegmentSchema = z.object({
    experimentId: z.string(),
    variant: z.string(),
    platform: z.string(),
    offersShown: z.number().int().nonnegative(),
    trialStarts: z.number().int().nonnegative(),
    firstValueReached: z.number().int().nonnegative(),
    paidConversions: z.number().int().nonnegative(),
    activatedPaidConversions: z.number().int().nonnegative(),
    cancellations: z.number().int().nonnegative(),
    refunds: z.number().int().nonnegative().nullable(),
    paidD30Retained: z.number().int().nonnegative().nullable(),
    offerToTrialRate: z.number().nonnegative().nullable(),
    trialToPaidRate: z.number().nonnegative().nullable(),
    activatedToPaidRate: z.number().nonnegative().nullable(),
    sampleSufficient: z.boolean(),
});
export const adminTrialExperimentMetricsSchema = z.object({
    window: z.string(),
    segments: z.array(trialExperimentSegmentSchema),
    note: z.string(),
});
//# sourceMappingURL=analytics.js.map